import { Fragment, type ReactNode } from 'react';
import { Box, HStack, chakra } from '@chakra-ui/react';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { MENU_BUTTON_COLOR } from '../ui/theme.ts';

// What can be done with the selected tile in edit mode (dashboard-editor L-11): a toolbar of icons
// next to it, in the menu's colours. Each button says what it does to a screen reader and, with the
// pointer on it, in a tooltip. Delete comes last, apart and in red.

export interface TileAction {
    readonly label: string;
    /** By Font Awesome name. */
    readonly icon: string;
    readonly onClick: () => void;
    readonly danger?: boolean;
}

/** The toolbar's height and width at most, for placing it next to the tile (`EditLayer`). */
export const TILE_ACTIONS_HEIGHT = 44;
export const TILE_ACTIONS_WIDTH = 300;

const DANGER_COLOR = '#ff6b6b';

const ActionButton = chakra('button', {
    base: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: 'md',
        cursor: 'pointer',
        _hover: { bg: 'casa.raised' },
        _active: { bg: 'casa.line' },
        _focusVisible: { outline: '2px solid', outlineColor: 'casa.accent', outlineOffset: '1px' },
    },
});

export function TileActions({ actions }: { actions: readonly TileAction[] }) : ReactNode {
    return (
        <HStack
            role="toolbar"
            aria-label="The selected tile"
            gap="0.5"
            p="1"
            height={`${TILE_ACTIONS_HEIGHT}px`}
            boxSizing="border-box"
            bg="casa.panel"
            borderWidth="1px"
            borderColor="casa.line"
            borderRadius="lg"
            boxShadow="lg"
        >
            {actions.map((action) => (
                <Fragment key={action.label}>
                    {action.danger ? <Box aria-hidden width="1px" alignSelf="stretch" my="1" mx="1" bg="casa.line" /> : null}
                    <ActionButton type="button" aria-label={action.label} title={action.label} onClick={action.onClick}>
                        <IconGlyph name={action.icon} size={18} color={action.danger ? DANGER_COLOR : MENU_BUTTON_COLOR} />
                    </ActionButton>
                </Fragment>
            ))}
        </HStack>
    );
}
